/*
 * ⚠️ ERZEUGT — nicht von Hand ändern.
 *
 * Quelle: settings/katalog.json + settings/texte-stage.json
 * Erzeuger: tools/settings-generieren.mjs (läuft in `npm run build` mit)
 *
 * Wer hier etwas ändern will, ändert die Quelle — ihre Texte über Relays Doku-Seite.
 * Der Bau prüft beide gegeneinander.
 */

export type EintragTyp = "text" | "passwort" | "schalter" | "auswahl" | "auswahlZahl" | "schieber";

export interface Auswahlwert {
  wert: string | number;
  text: { de: string; en: string; fr: string };
}

export interface KatalogEintrag {
  name: string;
  gruppe: string;
  typ: EintragTyp;
  vorgabe: string | number | boolean;
  /** Auswahlwerte — nur bei `auswahl` und `auswahlZahl`. */
  werte?: Auswahlwert[];
  /** Die Anschläge des Reglers — nur bei `schieber`. */
  min?: number;
  max?: number;
  /** Zeigt die App-Oberfläche diese Einstellung? */
  inOberflaeche: boolean;
  /**
   * Steht sie in den iPad-Einstellungen (`Settings.bundle`)?
   *
   * ⚠️ Der Name oben gilt **überall**: in der `Root.plist`, in `NSUserDefaults`, im Code und
   * in der App-Datenbank (Relay' `ClientSettingValue`). Bis zum 2026-09-17 trug jede
   * Einstellung dort einen zweiten, eigenen Namen — der ist gestrichen.
   */
  inIpadSettings: boolean;
  /** Darf eine Prüfung sie für sich setzen (Relay `exam/document.settings`)? Nie ein Passwort. */
  jePruefung: boolean;
}

export const EINTRAEGE: readonly KatalogEintrag[] = [
  {
    name: "server_protocol",
    gruppe: "server",
    typ: "auswahl",
    vorgabe: "relay",
    werte: [
      { wert: "relay", text: {"de":"Relay","en":"Relay","fr":"Relay"} },
      { wert: "classic", text: {"de":"Classic","en":"Classic","fr":"Classic"} },
    ],
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "server_url",
    gruppe: "server",
    typ: "text",
    vorgabe: "https://192.168.178.88:8111",
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "classic_login",
    gruppe: "server",
    typ: "text",
    vorgabe: "admin",
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "classic_password",
    gruppe: "server",
    typ: "passwort",
    vorgabe: "sgdrkp",
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "communication_login",
    gruppe: "server",
    typ: "text",
    vorgabe: "device",
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "communication_password",
    gruppe: "server",
    typ: "passwort",
    vorgabe: "2jwWqGxH388cmxr7Nyke",
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "auto_upload",
    gruppe: "server",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "video_audio",
    gruppe: "recording",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "save_photos_in_album",
    gruppe: "recording",
    typ: "schalter",
    vorgabe: false,
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "media_label",
    gruppe: "recording",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "settings_password",
    gruppe: "lock",
    typ: "passwort",
    vorgabe: "",
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "exit_pin",
    gruppe: "exit",
    typ: "passwort",
    vorgabe: "",
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "language",
    gruppe: "ui",
    typ: "auswahl",
    vorgabe: "system",
    werte: [
      { wert: "system", text: {"de":"Gerätesprache","en":"Device language","fr":"Langue de l’appareil"} },
      { wert: "de", text: {"de":"Deutsch","en":"Deutsch","fr":"Deutsch"} },
      { wert: "en", text: {"de":"English","en":"English","fr":"English"} },
      { wert: "fr", text: {"de":"Français","en":"Français","fr":"Français"} },
    ],
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "language_changeable",
    gruppe: "ui",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "ui_accent",
    gruppe: "ui",
    typ: "auswahl",
    vorgabe: "blau",
    werte: [
      { wert: "blau", text: {"de":"Blau","en":"Blue","fr":"Bleu"} },
      { wert: "gruen", text: {"de":"Grün","en":"Green","fr":"Vert"} },
      { wert: "rot", text: {"de":"Rot","en":"Red","fr":"Rouge"} },
      { wert: "orange", text: {"de":"Orange","en":"Orange","fr":"Orange"} },
      { wert: "lila", text: {"de":"Lila","en":"Purple","fr":"Violet"} },
      { wert: "tuerkis", text: {"de":"Türkis","en":"Teal","fr":"Turquoise"} },
      { wert: "grau", text: {"de":"Grau","en":"Grey","fr":"Gris"} },
    ],
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "ui_theme",
    gruppe: "ui",
    typ: "auswahl",
    vorgabe: "system",
    werte: [
      { wert: "system", text: {"de":"Wie das iPad","en":"Follow the iPad","fr":"Comme l’iPad"} },
      { wert: "light", text: {"de":"Hell","en":"Light","fr":"Clair"} },
      { wert: "dark", text: {"de":"Dunkel","en":"Dark","fr":"Sombre"} },
    ],
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "landscape_mode",
    gruppe: "ui",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "sheet_half_points",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "sheet_show_timer",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "station_comments_enabled",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "text_comments",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "sketch_comments",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "audio_comments",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "photo_comments",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "video_comments",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "colorful_text",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "show_task_points",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "show_sum_points",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "editable_task_points",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "actors_enabled",
    gruppe: "sheet",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "next_candidate_button",
    gruppe: "submit",
    typ: "auswahl",
    vorgabe: "scan",
    werte: [
      { wert: "scan", text: {"de":"QR-Scanner öffnen","en":"Open the QR scanner","fr":"Ouvrir le scanner QR"} },
      { wert: "next", text: {"de":"Nächster aus der Liste","en":"Next from the list","fr":"Suivant de la liste"} },
    ],
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "submit_haptics",
    gruppe: "submit",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "video_max_seconds",
    gruppe: "storage",
    typ: "auswahlZahl",
    vorgabe: 300,
    werte: [
      { wert: 60, text: {"de":"1 Minute","en":"1 minute","fr":"1 minute"} },
      { wert: 120, text: {"de":"2 Minuten","en":"2 minutes","fr":"2 minutes"} },
      { wert: 300, text: {"de":"5 Minuten","en":"5 minutes","fr":"5 minutes"} },
      { wert: 600, text: {"de":"10 Minuten","en":"10 minutes","fr":"10 minutes"} },
    ],
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: true,
  },
  {
    name: "deleted_files_retention_days",
    gruppe: "storage",
    typ: "auswahlZahl",
    vorgabe: 180,
    werte: [
      { wert: 30, text: {"de":"1 Monat","en":"1 month","fr":"1 mois"} },
      { wert: 90, text: {"de":"3 Monate","en":"3 months","fr":"3 mois"} },
      { wert: 180, text: {"de":"6 Monate","en":"6 months","fr":"6 mois"} },
      { wert: 365, text: {"de":"12 Monate","en":"12 months","fr":"12 mois"} },
    ],
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "device_name",
    gruppe: "device",
    typ: "text",
    vorgabe: "",
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "ntp_server",
    gruppe: "device",
    typ: "text",
    vorgabe: "time.apple.com",
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "min_brightness",
    gruppe: "device",
    typ: "schieber",
    vorgabe: 30,
    min: 10,
    max: 100,
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "max_brightness",
    gruppe: "device",
    typ: "schieber",
    vorgabe: 80,
    min: 10,
    max: 100,
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "error_reporting",
    gruppe: "device",
    typ: "schalter",
    vorgabe: true,
    inOberflaeche: true,
    inIpadSettings: true,
    jePruefung: false,
  },
  {
    name: "demo_install",
    gruppe: "demo",
    typ: "schalter",
    vorgabe: false,
    inOberflaeche: false,
    inIpadSettings: true,
    jePruefung: false,
  },
] as const;

/** Die Gruppen in ihrer Reihenfolge — sie gilt für beide Oberflächen. */
export const GRUPPEN: readonly { id: string }[] = [
  { id: "server" },
  { id: "recording" },
  { id: "lock" },
  { id: "exit" },
  { id: "ui" },
  { id: "sheet" },
  { id: "submit" },
  { id: "storage" },
  { id: "device" },
  { id: "demo" },
  { id: "about" },
] as const;

/**
 * Dieselben Einträge, nach Namen aufgeschlüsselt und `as const` — daraus leitet
 * `platform/einstellungen.ts` die **Typen** ab: welche Schlüssel es gibt und welchen Wert
 * jeder trägt.
 *
 * ⚠️ **Zwei Formen derselben Sache, mit Absicht.** `EINTRAEGE` ist die Liste in ihrer
 * Reihenfolge — die braucht jede Oberfläche, die sie zeichnet. `DATEN` ist die Nachschlagform
 * mit scharfen Literaltypen; ein `find()` über die Liste liefert die nicht.
 */
export const DATEN = {
  server_protocol: {
    typ: "auswahl",
    vorgabe: "relay",
    werte: ["relay", "classic"],
  },
  server_url: {
    typ: "text",
    vorgabe: "https://192.168.178.88:8111",
  },
  classic_login: {
    typ: "text",
    vorgabe: "admin",
  },
  classic_password: {
    typ: "passwort",
    vorgabe: "sgdrkp",
  },
  communication_login: {
    typ: "text",
    vorgabe: "device",
  },
  communication_password: {
    typ: "passwort",
    vorgabe: "2jwWqGxH388cmxr7Nyke",
  },
  auto_upload: {
    typ: "schalter",
    vorgabe: true,
  },
  video_audio: {
    typ: "schalter",
    vorgabe: true,
  },
  save_photos_in_album: {
    typ: "schalter",
    vorgabe: false,
  },
  media_label: {
    typ: "schalter",
    vorgabe: true,
  },
  settings_password: {
    typ: "passwort",
    vorgabe: "",
  },
  exit_pin: {
    typ: "passwort",
    vorgabe: "",
  },
  language: {
    typ: "auswahl",
    vorgabe: "system",
    werte: ["system", "de", "en", "fr"],
  },
  language_changeable: {
    typ: "schalter",
    vorgabe: true,
  },
  ui_accent: {
    typ: "auswahl",
    vorgabe: "blau",
    werte: ["blau", "gruen", "rot", "orange", "lila", "tuerkis", "grau"],
  },
  ui_theme: {
    typ: "auswahl",
    vorgabe: "system",
    werte: ["system", "light", "dark"],
  },
  landscape_mode: {
    typ: "schalter",
    vorgabe: true,
  },
  sheet_half_points: {
    typ: "schalter",
    vorgabe: true,
  },
  sheet_show_timer: {
    typ: "schalter",
    vorgabe: true,
  },
  station_comments_enabled: {
    typ: "schalter",
    vorgabe: true,
  },
  text_comments: {
    typ: "schalter",
    vorgabe: true,
  },
  sketch_comments: {
    typ: "schalter",
    vorgabe: true,
  },
  audio_comments: {
    typ: "schalter",
    vorgabe: true,
  },
  photo_comments: {
    typ: "schalter",
    vorgabe: true,
  },
  video_comments: {
    typ: "schalter",
    vorgabe: true,
  },
  colorful_text: {
    typ: "schalter",
    vorgabe: true,
  },
  show_task_points: {
    typ: "schalter",
    vorgabe: true,
  },
  show_sum_points: {
    typ: "schalter",
    vorgabe: true,
  },
  editable_task_points: {
    typ: "schalter",
    vorgabe: true,
  },
  actors_enabled: {
    typ: "schalter",
    vorgabe: true,
  },
  next_candidate_button: {
    typ: "auswahl",
    vorgabe: "scan",
    werte: ["scan", "next"],
  },
  submit_haptics: {
    typ: "schalter",
    vorgabe: true,
  },
  video_max_seconds: {
    typ: "auswahlZahl",
    vorgabe: 300,
    werte: [60, 120, 300, 600],
  },
  deleted_files_retention_days: {
    typ: "auswahlZahl",
    vorgabe: 180,
    werte: [30, 90, 180, 365],
  },
  device_name: {
    typ: "text",
    vorgabe: "",
  },
  ntp_server: {
    typ: "text",
    vorgabe: "time.apple.com",
  },
  min_brightness: {
    typ: "schieber",
    vorgabe: 30,
    min: 10,
    max: 100,
  },
  max_brightness: {
    typ: "schieber",
    vorgabe: 80,
    min: 10,
    max: 100,
  },
  error_reporting: {
    typ: "schalter",
    vorgabe: true,
  },
  demo_install: {
    typ: "schalter",
    vorgabe: false,
  },
} as const;

/** Der Name jeder Einstellung — die Schlüssel des Katalogs. */
export type Schluessel = keyof typeof DATEN;
